import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Work as StaffIcon,
  People as UserIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useAuth } from "../AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { useStores } from "../hooks/useApi";

/**
 * ユーザーリストアイテムインターフェース
 */
interface UserListItem {
  id: string;
  username: string;
  email: string;
  store_id?: string;
  firebase_uid?: string;
  display_name?: string;
  photo_url?: string;
  roles: string[];
}

/**
 * ユーザーリストレスポンス
 */
interface UserListResponse {
  users: UserListItem[];
  total: number;
}

/**
 * ロール設定定義
 */
const roleConfig = [
  {
    value: "user",
    label: "ユーザー",
    icon: UserIcon,
    color: "#1976d2",
  },
  {
    value: "staff",
    label: "スタッフ",
    icon: StaffIcon,
    color: "#2196f3",
  },
  {
    value: "admin",
    label: "管理者",
    icon: AdminIcon,
    color: "#d32f2f",
  },
];

/**
 * 管理者用ユーザー一覧ページ
 */
const AdminUsersList: React.FC = () => {
  const theme = useTheme();
  const { currentUser, userProfile } = useAuth();
  const { showSuccess, showError } = useNotification();

  // 状態管理
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [editStoreId, setEditStoreId] = useState<string>("");

  // API hooks
  const { data: stores, isLoading: storesLoading } = useStores();

  const isAdmin = userProfile?.roles.includes("admin");

  /**
   * ユーザー一覧を取得
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) {
        throw new Error("認証トークンが取得できません");
      }

      console.log(
        "[DEBUG] AdminUsersList - Token:",
        token.substring(0, 50) + "..."
      );
      console.log("[DEBUG] AdminUsersList - User Profile:", userProfile);

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080/api/v1";
      const response = await fetch(`${apiUrl}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("[DEBUG] AdminUsersList - Response status:", response.status);
      console.log(
        "[DEBUG] AdminUsersList - Response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("ユーザー一覧にアクセスする権限がありません");
        }
        throw new Error("ユーザー一覧の取得に失敗しました");
      }

      const data: UserListResponse = await response.json();
      setUsers(data.users || []);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      showError(error.message || "ユーザー一覧の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [currentUser, userProfile, showError, setUsers]);

  // 初期化と自動リロード
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin,fetchUsers]);

  /**
   * ページ変更ハンドラ
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * 行数変更ハンドラ
   */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * ロール表示用チップ
   */
  const getRoleChip = (role: string) => {
    const config = roleConfig.find((r) => r.value === role);
    return (
      <Chip
        key={role}
        label={config?.label || role}
        size="small"
        sx={{
          backgroundColor: alpha(config?.color || "#000", 0.1),
          color: config?.color || "#000",
          fontWeight: 600,
          mr: 0.5,
          mb: 0.5,
        }}
      />
    );
  };

  /**
   * ユーザー編集ダイアログを開く
   */
  const handleOpenEditDialog = (user: UserListItem) => {
    setSelectedUser(user);
    setEditRoles(user.roles);
    setEditStoreId(user.store_id || "");
    setEditDialogOpen(true);
  };

  /**
   * ユーザー編集ダイアログを閉じる
   */
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditRoles([]);
    setEditStoreId("");
  };

  /**
   * ロール編集を保存
   */
  const handleSaveRoles = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const token = await currentUser?.getIdToken();
      if (!token) {
        throw new Error("認証トークンが取得できません");
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080/api/v1";
      const response = await fetch(`${apiUrl}/admin/users/roles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: selectedUser.firebase_uid || selectedUser.id,
          roles: editRoles,
          store_id: editStoreId || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.details
          ? `${errorData.message}: ${errorData.details}`
          : errorData.message || "ロール更新に失敗しました";
        throw new Error(errorMessage);
      }

      showSuccess("ユーザーのロールを更新しました");
      handleCloseEditDialog();
      await fetchUsers();
    } catch (error: any) {
      console.error("Error updating user:", error);
      showError(error.message || "ロール更新に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  /**
   * ロール選択を切り替え
   */
  const handleRoleToggle = (role: string) => {
    setEditRoles((prev) => {
      if (prev.includes(role)) {
        if (prev.length === 1) {
          showError("少なくとも1つのロールは必要です");
          return prev;
        }
        return prev.filter((r) => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  // 管理者権限がない場合はエラー表示
  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6">アクセス拒否</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            ユーザー管理機能にアクセスするには管理者権限が必要です。
          </Typography>
        </Alert>
      </Container>
    );
  }

  // フィルタリング後のユーザー
  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ページングされたユーザー
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <UserIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            ユーザー管理
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          全ユーザーの一覧表示とロール管理を行います
        </Typography>
      </Box>

      {/* 検索・リロードバー */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="ユーザー名またはメールアドレスで検索..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            更新
          </Button>
        </Box>
      </Paper>

      {/* ユーザーテーブル */}
      {loading && paginatedUsers.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead
                sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>ユーザー</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>メール</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ロール</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>店舗ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    アクション
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05
                        ),
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          src={user.photo_url}
                          alt={user.display_name || user.username}
                          sx={{ width: 40, height: 40 }}
                        >
                          {(user.display_name || user.username).charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user.display_name || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {user.roles.map((role) => getRoleChip(role))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.store_id ? (
                        <Chip
                          label={user.store_id}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          未割り当て
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(user)}
                        title="編集"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ページネーション */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="1ページあたり"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} / ${count}`
            }
          />
        </>
      )}

      {/* ユーザー編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon color="primary" />
            <Typography>ユーザーロール・店舗編集</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedUser && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  ユーザー名
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.display_name || selectedUser.username}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  メール
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUser.email}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  ロール
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {roleConfig.map((role) => (
                    <Box
                      key={role.value}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: editRoles.includes(role.value)
                          ? theme.palette.primary.main
                          : theme.palette.divider,
                        borderRadius: 1,
                        cursor: "pointer",
                        backgroundColor: editRoles.includes(role.value)
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          ),
                        },
                      }}
                      onClick={() => handleRoleToggle(role.value)}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            border: "2px solid",
                            borderColor: editRoles.includes(role.value)
                              ? role.color
                              : theme.palette.divider,
                            borderRadius: "4px",
                            backgroundColor: editRoles.includes(role.value)
                              ? role.color
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {editRoles.includes(role.value) && (
                            <Typography
                              sx={{ color: "white", fontWeight: 700 }}
                            >
                              ✓
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role.label}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  店舗割り当て
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>店舗</InputLabel>
                  <Select
                    value={editStoreId}
                    onChange={(e) => setEditStoreId(e.target.value)}
                    label="店舗"
                    disabled={storesLoading}
                  >
                    <MenuItem value="">
                      <em>未割り当て</em>
                    </MenuItem>
                    {storesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        読み込み中...
                      </MenuItem>
                    ) : (
                      stores?.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name} ({store.id})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSaveRoles}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsersList;
